// src/pages/StudentChatPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import StudentLayout from "../components/student/StudentLayout";
import {
  Box,
  Typography,
  Divider,
  List,
  ListItemButton,
  Avatar,
  TextField,
  Button,
  IconButton,
  Tooltip,
  InputAdornment,
  Chip,
  Menu,
  MenuItem,
  Popover,
  CircularProgress,
  useMediaQuery,
  Breadcrumbs,
  Link,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TelegramIcon from "@mui/icons-material/Telegram";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import KeyboardVoiceRoundedIcon from "@mui/icons-material/KeyboardVoiceRounded";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EmailIcon from "@mui/icons-material/Email";
import { getAuth } from "firebase/auth";
import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  addDoc,
  updateDoc,
  writeBatch,
  serverTimestamp,
  increment,
  deleteDoc,
  getDocs,
  setDoc,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebaseConfig";
import { useTranslation } from "react-i18next";
import EmojiPicker from "emoji-picker-react";

/**
 * Firestore assumptions (matches your TeacherChatPage):
 * conversations/{id} fields:
 *  - teacherId (teacher AUTH uid)
 *  - studentId (student Firestore docId)
 *  - teacherName, teacherPhotoURL
 *  - lastMessageText, lastMessageAt, lastSenderId
 *  - unreadCountStudent, unreadCountTeacher
 *
 * conversations/{id}/messages/{mid} fields:
 *  - senderId (teacher uid OR student docId)
 *  - text, createdAt
 */

function ChatBubble({ isMine, children, time, status, progress, seen }) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: isMine ? "flex-end" : "flex-start",
        maxWidth: "100%",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isMine ? "row-reverse" : "row",
          alignItems: "flex-end",
          gap: 0.9,
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: 0.6,
            maxWidth: { xs: "100%", sm: "78%", md: "620px" },
            minWidth: 0,
            px: 1.8,
            py: 1.05,
            borderRadius: isMine ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
            border: isMine ? "none" : "1px solid #dfe8f5",
            fontSize: 13,
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
            hyphens: "auto",
            bgcolor: isMine ? "#eaf4ff" : "#ffffff",
            color: "#152355",
            boxShadow: "0 5px 16px rgba(8, 26, 74, 0.035)",
          }}
        >
          <Box sx={{ minWidth: 0 }}>{children}</Box>
          {status === "uploading" && (
            <Typography sx={{ fontSize: 11, color: "#6f89b8" }}>
              {progress != null ? t("Uploading {{p}}%", { p: Math.max(1, Math.min(100, Math.round(progress))) }) : t("Sending...")}
            </Typography>
          )}
          {status === "failed" && (
            <Typography sx={{ fontSize: 11, color: "#ef4444" }}>{t("Failed to send")}</Typography>
          )}
        </Box>
        {!!time && (
          <Typography
            sx={{
              color: "#49699f",
              fontSize: 11,
              fontWeight: 500,
              lineHeight: 1,
              mb: 0.45,
              flexShrink: 0,
            }}
          >
            {time}
          </Typography>
        )}
      </Box>
      {isMine && seen && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mt: 0.5,
            px: 0.2,
            alignSelf: "flex-end",
          }}
        >
          <VisibilityOutlinedIcon sx={{ fontSize: 13, color: "#0068ff" }} />
        </Box>
      )}
    </Box>
  );
}

const initialsFromName = (fullName = "") => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const first = parts[0][0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  const joined = `${first}${last || ""}`.trim();
  return joined ? joined.toUpperCase() : "?";
};

const AVATAR_COLORS = [
  { bg: "#5295ef", color: "#ffffff" },
  { bg: "#f281aa", color: "#ffffff" },
  { bg: "#05d9c5", color: "#ffffff" },
  { bg: "#5866cd", color: "#ffffff" },
];

const avatarColorsForName = (fullName = "") => {
  if (!fullName) return AVATAR_COLORS[0];
  const hash = fullName
    .split("")
    .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const MAX_IMAGE_BYTES = 25 * 1024 * 1024;
const MAX_AUDIO_BYTES = 20 * 1024 * 1024;
const MAX_AUDIO_MS = 120 * 1000;
const MAX_VIDEO_BYTES = 1024 * 1024 * 1024;

export default function StudentChatPage() {
  const { t, i18n } = useTranslation();
  const auth = getAuth();
  const isRtl = (i18n.resolvedLanguage || i18n.language || "").toLowerCase().startsWith("ar");

  const isMobile = useMediaQuery("(max-width:900px)");
  const [mobileView, setMobileView] = useState("list"); // "list" | "chat"

  const [studentDocId, setStudentDocId] = useState("");
  const [studentProfile, setStudentProfile] = useState({ name: "", photoURL: "" });
  const [allTeachers, setAllTeachers] = useState([]); // unified list: all teachers of student's groups
  const [search, setSearch] = useState("");

  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);

  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesHasMore, setMessagesHasMore] = useState(true);
  const [messagesCursor, setMessagesCursor] = useState(null);
  const [initialMessagesLoaded, setInitialMessagesLoaded] = useState(false);
  const [messageInput, setMessageInput] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({}); // messageId -> percent (0-100)
  const [uploadPreviews, setUploadPreviews] = useState({}); // messageId -> { url, type }

  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [messageMenuAnchor, setMessageMenuAnchor] = useState(null);
  const [activeMessageId, setActiveMessageId] = useState(null);
  const [activeMessageSenderId, setActiveMessageSenderId] = useState(null);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);

  const messagesListRef = useRef(null);
  const shouldScrollToBottomRef = useRef(false);
  const isAtBottomRef = useRef(true);
  const prependAdjustRef = useRef(null);
  const fileInputRef = useRef(null);
  const composerInputRef = useRef(null);
  const mountedRef = useRef(true);
  const recorderRef = useRef(null);
  const recordChunksRef = useRef([]);
  const recordSecondsRef = useRef(0);
  const recordTimerRef = useRef(null);
  const recordTimeoutRef = useRef(null);
  const recordStreamRef = useRef(null);
  const recordCancelRef = useRef(false);
  const recordFinalizedRef = useRef(false);
  const isEmojiPickerOpen = Boolean(emojiAnchorEl);

  const handleEmojiPick = (emojiData) => {
    const emoji = emojiData?.emoji || "";
    if (!emoji) return;

    const inputEl = composerInputRef.current;
    if (inputEl && typeof inputEl.selectionStart === "number") {
      const start = inputEl.selectionStart;
      const end = inputEl.selectionEnd ?? start;
      const next = `${messageInput.slice(0, start)}${emoji}${messageInput.slice(end)}`;
      setMessageInput(next);

      requestAnimationFrame(() => {
        inputEl.focus();
        const cursor = start + emoji.length;
        inputEl.setSelectionRange(cursor, cursor);
      });
      return;
    }

    setMessageInput((prev) => `${prev}${emoji}`);
  };

  const safeTime = (ts) => {
    try {
      if (!ts) return "";
      if (typeof ts.toDate === "function") {
        const d = ts.toDate();
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      }
      const d = typeof ts === "number" ? new Date(ts) : new Date(ts);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  const safeDateSeparator = (ts) => {
    try {
      if (!ts) return "";
      const d = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
    } catch {
      return "";
    }
  };

  const labelForType = (type, fallback) => {
    if (type === "image") return t("Photo");
    if (type === "video") return t("Video");
    if (type === "audio") return t("Voice message");
    return fallback || t("Message");
  };

  const scrollToBottom = (behavior = "auto") => {
    const el = messagesListRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  };

  const normalizeMessageTs = (msg) => {
    const ts = msg?.createdAt;
    if (!ts) return 0;
    if (typeof ts.toMillis === "function") return ts.toMillis();
    if (typeof ts.seconds === "number") return ts.seconds * 1000;
    return 0;
  };

  // 1) Resolve student Firestore docId + fetch student's teacher list (teachers whose groupIds overlap)
  useEffect(() => {
    const run = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        // Student docId (IMPORTANT: conversations.studentId uses student docId)
        const sQ = query(collection(db, "students"), where("uid", "==", user.uid));
        const sSnap = await getDocs(sQ);
        if (sSnap.empty) return;

        const sDoc = sSnap.docs[0];
        const sId = sDoc.id;
        setStudentDocId(sId);

        const sData = sDoc.data() || {};
        const enrollments = Array.isArray(sData.enrollments) ? sData.enrollments : [];
        const studentName = `${sData.firstName || ""} ${sData.lastName || ""}`.trim();
        setStudentProfile({
          name: studentName || sData.name || auth.currentUser?.displayName || t("Student"),
          photoURL: sData.photoURL || sData.imageUrl || auth.currentUser?.photoURL || "",
        });

        const groupIds = enrollments
          .map((e) => e && e.groupId)
          .filter(Boolean);

        if (!groupIds.length) {
          setAllTeachers([]);
          return;
        }

        // Teachers contain groupIds: [..] (as you already use in Teacher side)
        // Firestore allows array-contains-any max 10 values -> chunk.
        const chunks = [];
        for (let i = 0; i < groupIds.length; i += 10) {
          chunks.push(groupIds.slice(i, i + 10));
        }

        const teachers = [];
        const seen = new Set();

        for (const chunk of chunks) {
          const tQ = query(collection(db, "teachers"), where("groupIds", "array-contains-any", chunk));
          const tSnap = await getDocs(tQ);

          tSnap.forEach((d) => {
            const t = d.data() || {};
            const teacherUid = t.uid || ""; // teacher AUTH uid
            if (!teacherUid) return;
            if (seen.has(teacherUid)) return;
            seen.add(teacherUid);

            const firstName = t.firstName || "";
            const lastName = t.lastName || "";
            teachers.push({
              teacherId: teacherUid,
              teacherName: `${firstName} ${lastName}`.trim() || t("Teacher"),
              teacherPhotoURL: t.photoURL || t.imageUrl || "",
            });
          });
        }

        setAllTeachers(teachers);
      } catch (e) {
        console.error("StudentChatPage init failed:", e);
        setAllTeachers([]);
      }
    };

    run();
  }, [auth, t]);

  // 2) Realtime inbox for this student
  const conversationsQuery = useMemo(() => {
    if (!studentDocId) return null;
    return query(
      collection(db, "conversations"),
      where("studentId", "==", studentDocId),
      orderBy("lastMessageAt", "desc")
    );
  }, [studentDocId]);

  useEffect(() => {
    if (!conversationsQuery) return;

    const unsub = onSnapshot(
      conversationsQuery,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setConversations(list);

        // keep active conversation fresh
        if (activeConversation?.id) {
          const updated = list.find((x) => x.id === activeConversation.id);
          if (updated) setActiveConversation(updated);
        }
      },
      (err) => console.error("Failed to fetch conversations:", err)
    );

    return () => unsub();
  }, [conversationsQuery, activeConversation?.id]);

  // 3) Merge teachers list with conversations (so all teachers appear even if no conversation yet)
  const allTeacherRows = useMemo(() => {
    const convoByTeacher = new Map();
    conversations.forEach((c) => {
      if (c.teacherId) convoByTeacher.set(c.teacherId, c);
    });

    const merged = allTeachers.map((t) => {
      const convo = convoByTeacher.get(t.teacherId);
      return {
        teacherId: t.teacherId,
        teacherName: t.teacherName,
        teacherPhotoURL: t.teacherPhotoURL,
        conversation: convo || null,
        lastMessageText: convo?.lastMessageText || "",
        lastMessageAt: convo?.lastMessageAt || null,
        unreadCountStudent: convo?.unreadCountStudent || 0,
        lastMessageType: convo?.lastMessageType || "text",
        convoId: convo?.id || null,
      };
    });

    merged.sort((a, b) => {
      const aT = a.lastMessageAt?.toMillis?.() || 0;
      const bT = b.lastMessageAt?.toMillis?.() || 0;
      if (bT !== aT) return bT - aT;
      return (a.teacherName || "").localeCompare(b.teacherName || "");
    });

    return merged;
  }, [allTeachers, conversations]);

  const filteredTeachers = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return allTeacherRows;

    return allTeacherRows.filter((row) => {
      const name = (row.teacherName || "").toLowerCase();
      const last = (row.lastMessageText || "").toLowerCase();
      return name.includes(s) || last.includes(s);
    });
  }, [allTeacherRows, search]);

  const previewText = (row) => {
    if (row.lastMessageType && row.lastMessageType !== "text") {
      return labelForType(row.lastMessageType, row.lastMessageText);
    }
    return row.lastMessageText ? row.lastMessageText : t("No messages yet");
  };

  const fetchMoreMessages = async () => {
    if (!activeConversation?.id || messagesLoading || !messagesHasMore || !messagesCursor) return;
    const el = messagesListRef.current;
    if (el) {
      prependAdjustRef.current = { prevHeight: el.scrollHeight, prevTop: el.scrollTop };
    }
    setMessagesLoading(true);
    try {
      const msgRef = collection(db, "conversations", activeConversation.id, "messages");
      const q = query(msgRef, orderBy("createdAt", "desc"), startAfter(messagesCursor), limit(10));
      const snap = await getDocs(q);
      const docs = snap.docs;
      const older = docs.map((d) => ({ id: d.id, ...d.data() })).reverse();
      setMessages((prev) => [...older, ...prev]);
      setMessagesCursor(docs[docs.length - 1] || messagesCursor);
      setMessagesHasMore(docs.length === 10);
    } catch (err) {
      console.error("Failed to fetch older messages:", err);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleMessagesScroll = () => {
    const el = messagesListRef.current;
    if (!el) return;
    const bottomGap = el.scrollHeight - el.scrollTop - el.clientHeight;
    isAtBottomRef.current = bottomGap < 40;
    if (el.scrollTop <= 40 && messagesHasMore && !messagesLoading) {
      fetchMoreMessages();
    }
  };

  // 4) Initial load + live recent updates
  useEffect(() => {
    if (!activeConversation?.id) {
      setMessages([]);
      setMessagesHasMore(true);
      setMessagesCursor(null);
      setInitialMessagesLoaded(false);
      return;
    }

    let cancelled = false;
    let unsub = () => {};

    const loadInitial = async () => {
      setMessagesLoading(true);
      try {
        const msgRef = collection(db, "conversations", activeConversation.id, "messages");
        const q = query(msgRef, orderBy("createdAt", "desc"), limit(10));
        const snap = await getDocs(q);
        if (cancelled) return;
        const docs = snap.docs;
        const list = docs.map((d) => ({ id: d.id, ...d.data() })).reverse();
        setMessages(list);
        setMessagesCursor(docs[docs.length - 1] || null);
        setMessagesHasMore(docs.length === 10);
        setInitialMessagesLoaded(true);
        shouldScrollToBottomRef.current = true;
      } catch (err) {
        console.error("Failed to fetch initial messages:", err);
      } finally {
        if (!cancelled) setMessagesLoading(false);
      }
    };

    loadInitial();

    const msgRef = collection(db, "conversations", activeConversation.id, "messages");
    const recentQuery = query(msgRef, orderBy("createdAt", "desc"), limit(10));
    unsub = onSnapshot(
      recentQuery,
      (snap) => {
        const recent = snap.docs.map((d) => ({ id: d.id, ...d.data() })).reverse();
        const wasAtBottom = isAtBottomRef.current;
        setMessages((prev) => {
          if (!prev.length) return recent;
          const map = new Map(prev.map((m) => [m.id, m]));
          recent.forEach((m) => map.set(m.id, m));
          const merged = Array.from(map.values());
          merged.sort((a, b) => normalizeMessageTs(a) - normalizeMessageTs(b));
          return merged;
        });
        if (wasAtBottom) {
          shouldScrollToBottomRef.current = true;
        }
      },
      (err) => console.error("Failed to fetch recent messages:", err)
    );

    return () => {
      cancelled = true;
      unsub();
    };
  }, [activeConversation?.id]);

  // 5) Mark read for student when opening a conversation
  useEffect(() => {
    const markRead = async () => {
      if (!activeConversation?.id) return;
      try {
        await updateDoc(doc(db, "conversations", activeConversation.id), {
          unreadCountStudent: 0,
        });
      } catch (e) {
        console.error("Failed to mark read:", e);
      }
    };
    markRead();
  }, [activeConversation?.id]);

  // 5b) Mark teacher messages as seen when student views them
  useEffect(() => {
    if (!activeConversation?.id || !studentDocId || !messages.length) return;
    const unseen = messages.filter(
      (m) => m.senderId && m.senderId !== studentDocId && !m.seenByStudentAt
    );
    if (!unseen.length) return;
    const markSeen = async () => {
      try {
        const batch = writeBatch(db);
        unseen.forEach((m) => {
          batch.update(doc(db, "conversations", activeConversation.id, "messages", m.id), {
            seenByStudentAt: serverTimestamp(),
          });
        });
        await batch.commit();
      } catch (e) {
        console.error("Failed to mark messages seen:", e);
      }
    };
    markSeen();
  }, [messages, activeConversation?.id, studentDocId]);

  // 6) Adjust scroll after prepending older messages or when at bottom
  useEffect(() => {
    if (!initialMessagesLoaded) return;
    const el = messagesListRef.current;
    if (!el) return;
    if (prependAdjustRef.current) {
      const { prevHeight, prevTop } = prependAdjustRef.current;
      prependAdjustRef.current = null;
      requestAnimationFrame(() => {
        const newHeight = el.scrollHeight;
        el.scrollTop = newHeight - prevHeight + prevTop;
      });
      return;
    }
    if (shouldScrollToBottomRef.current) {
      shouldScrollToBottomRef.current = false;
      requestAnimationFrame(() => scrollToBottom("auto"));
    }
  }, [messages, initialMessagesLoaded]);

  const groupedMessages = useMemo(() => {
    const out = [];
    let lastSep = "";

    for (const m of messages) {
      const sep = safeDateSeparator(m.createdAt);
      if (sep && sep !== lastSep) {
        out.push({ type: "sep", id: `sep-${sep}`, label: sep });
        lastSep = sep;
      }
      out.push({ type: "msg", ...m });
    }

    return out;
  }, [messages]);

  // 7) Open or create conversation (deterministic id same as TeacherChatPage)
  const openOrCreateConversation = async (teacherRow) => {
    try {
      if (!studentDocId || !teacherRow?.teacherId) return;

      // 1) Find existing conversation for this teacher+student
      const qConvo = query(
        collection(db, "conversations"),
        where("teacherId", "==", teacherRow.teacherId),
        where("studentId", "==", studentDocId)
      );
      const snap = await getDocs(qConvo);

      if (!snap.empty) {
        const d = snap.docs[0];
        setActiveConversation({ id: d.id, ...d.data() });
        if (isMobile) setMobileView("chat");
        return;
      }

      // 2) Create new conversation doc (same pattern as teacher page)
      const convoId = `${teacherRow.teacherId}_${studentDocId}`;
      const convoRef = doc(db, "conversations", convoId);

      await setDoc(
        convoRef,
        {
          teacherId: teacherRow.teacherId,
          studentId: studentDocId,

          participantIds: [teacherRow.teacherId, studentDocId],

          teacherName: teacherRow.teacherName?.trim() || t("Teacher"),
          teacherPhotoURL: teacherRow.teacherPhotoURL || "",

          lastMessageText: "",
          lastMessageAt: serverTimestamp(),
          lastSenderId: "",
          lastMessageType: "text",

          unreadCountTeacher: 0,
          unreadCountStudent: 0,
        },
        { merge: true }
      );

      setActiveConversation({
        id: convoId,
        teacherId: teacherRow.teacherId,
        studentId: studentDocId,
        participantIds: [teacherRow.teacherId, studentDocId],
        teacherName: teacherRow.teacherName?.trim() || t("Teacher"),
        teacherPhotoURL: teacherRow.teacherPhotoURL || "",
        lastMessageText: "",
        lastMessageAt: null,
        lastSenderId: "",
        lastMessageType: "text",
        unreadCountTeacher: 0,
        unreadCountStudent: 0,
      });

      if (isMobile) setMobileView("chat");
    } catch (e) {
      console.error("openOrCreateConversation failed:", e);
    }
  };

  // 8) Send message (student senderId must be studentDocId)
  const handleSend = async () => {
    if (!studentDocId || !activeConversation?.id) return;

    const text = messageInput.trim();
    if (!text) return;

    setSending(true);
    try {
      const convoRef = doc(db, "conversations", activeConversation.id);
      const msgRef = collection(db, "conversations", activeConversation.id, "messages");

      await addDoc(msgRef, {
        senderId: studentDocId,
        text,
        createdAt: serverTimestamp(),
      });

      await updateDoc(convoRef, {
        lastMessageText: text,
        lastMessageAt: serverTimestamp(),
        lastSenderId: studentDocId,
        lastMessageType: "text",
        unreadCountTeacher: increment(1),
      });

      setMessageInput("");
    } catch (e) {
      console.error("Failed to send message:", e);
    } finally {
      setSending(false);
    }
  };

  const handleUploadFile = async (file, durationMs = null) => {
    if (!studentDocId || !activeConversation?.id || !file) return;

    const mime = file.type || "";
    const isImage = mime.startsWith("image/");
    const isVideo = mime.startsWith("video/");
    const isAudio = mime.startsWith("audio/");
    const type = isImage ? "image" : isVideo ? "video" : isAudio ? "audio" : "text";

    if (!isImage && !isVideo && !isAudio) {
      console.warn("Unsupported file type");
      return;
    }

    if (isVideo && file.size > MAX_VIDEO_BYTES) {
      window.alert(t("Video is too large (max 1GB)."));
      return;
    }
    if (isImage && file.size > MAX_IMAGE_BYTES) {
      window.alert(t("Image is too large."));
      return;
    }
    if (isAudio && file.size > MAX_AUDIO_BYTES) {
      window.alert(t("Audio file is too large."));
      return;
    }
    if (isAudio && durationMs && durationMs > MAX_AUDIO_MS) {
      window.alert(t("Audio is longer than 120 seconds."));
      return;
    }

    const createPreviewUrl = () => {
      if (isImage || isVideo) {
        return URL.createObjectURL(file);
      }
      return "";
    };

    const cleanupPreview = (id) => {
      setUploadPreviews((prev) => {
        const existing = prev[id];
        if (existing?.url) {
          URL.revokeObjectURL(existing.url);
        }
        const next = { ...prev };
        delete next[id];
        return next;
      });
    };

    try {
      setUploading(true);
      const convoRef = doc(db, "conversations", activeConversation.id);
      const msgRef = collection(db, "conversations", activeConversation.id, "messages");
      const newMsgRef = doc(msgRef);
      const previewUrl = createPreviewUrl();

      await setDoc(newMsgRef, {
        senderId: studentDocId,
        type,
        text: "",
        url: "",
        thumbnailUrl: "",
        mimeType: mime,
        size: file.size || 0,
        durationMs: durationMs || null,
        status: "uploading",
        createdAt: serverTimestamp(),
      });
      if (previewUrl) {
        setUploadPreviews((prev) => ({
          ...prev,
          [newMsgRef.id]: { url: previewUrl, type },
        }));
      }

      const storagePath = `conversations/${activeConversation.id}/${newMsgRef.id}/${file.name}`;
      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          if (!snapshot?.totalBytes) return;
          const pct = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress((prev) => ({ ...prev, [newMsgRef.id]: pct }));
        },
        async (error) => {
          console.error("Upload failed:", error);
          try {
            await updateDoc(newMsgRef, { status: "failed" });
          } catch {
            /* ignore */
          }
          cleanupPreview(newMsgRef.id);
          setUploadProgress((prev) => {
            const cp = { ...prev };
            delete cp[newMsgRef.id];
            return cp;
          });
          setUploading(false);
        },
        async () => {
          try {
            const url = await getDownloadURL(storageRef);
            await updateDoc(newMsgRef, {
              url,
              status: "sent",
            });

            await updateDoc(convoRef, {
              lastMessageText: labelForType(type, ""),
              lastMessageType: type,
              lastMessageAt: serverTimestamp(),
              lastSenderId: studentDocId,
              unreadCountTeacher: increment(1),
            });
          } catch (e) {
            console.error("Failed to finalize upload:", e);
            await updateDoc(newMsgRef, { status: "failed" });
          }
          cleanupPreview(newMsgRef.id);
          setUploadProgress((prev) => {
            const cp = { ...prev };
            delete cp[newMsgRef.id];
            return cp;
          });
          setUploading(false);
        }
      );
    } catch (e) {
      console.error("Failed to upload file:", e);
      setUploading(false);
    }
  };

  const handleAttachmentChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      handleUploadFile(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const clearRecordingTimers = () => {
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    if (recordTimeoutRef.current) {
      clearTimeout(recordTimeoutRef.current);
      recordTimeoutRef.current = null;
    }
  };

  const stopRecordingStream = () => {
    if (recordStreamRef.current) {
      recordStreamRef.current.getTracks().forEach((t) => t.stop());
      recordStreamRef.current = null;
    }
  };

const stopRecording = (options = {}) => {
  if (!recorderRef.current) return;
  recordCancelRef.current = options.cancel === true;
   recordFinalizedRef.current = false;
  clearRecordingTimers();
  try {
    if (recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    } catch {
      // ignore
    }
    if (options.cancel) {
      // immediate cleanup for cancel to avoid stale UI
      recordChunksRef.current = [];
      setRecording(false);
      setRecordSeconds(0);
      recordSecondsRef.current = 0;
      stopRecordingStream();
  }

  // Fallback: if onstop never fires (browser quirk), finalize after a short delay
  if (!options.cancel) {
    setTimeout(() => {
      if (recordFinalizedRef.current || recordCancelRef.current) return;
      if (!recordChunksRef.current.length) return;
      try {
        const mime = recorderRef.current?.mimeType || "audio/webm";
        const blob = new Blob(recordChunksRef.current, { type: mime });
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: blob.type || "audio/webm" });
        recordFinalizedRef.current = true;
        handleUploadFile(file, Math.min(recordSecondsRef.current, MAX_AUDIO_MS / 1000) * 1000);
      } catch (err) {
        console.error("Fallback finalize failed:", err);
      } finally {
        setRecording(false);
        setRecordSeconds(0);
        recordSecondsRef.current = 0;
        recordChunksRef.current = [];
        stopRecordingStream();
      }
    }, 1200);
  }
};

const startRecording = async () => {
  if (recording) return;
  if (!activeConversation?.id) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  recordStreamRef.current = stream;
  const rec = new MediaRecorder(stream);
  recorderRef.current = rec;
  recordChunksRef.current = [];
  recordCancelRef.current = false;
  recordFinalizedRef.current = false;
  recordSecondsRef.current = 0;
  setRecordSeconds(0);

  rec.ondataavailable = (e) => {
    if (e.data?.size > 0) recordChunksRef.current.push(e.data);
      };

      rec.onstop = () => {
        clearRecordingTimers();
        stopRecordingStream();
        if (!mountedRef.current) return;
        setRecording(false);
        const wasCancelled = recordCancelRef.current;
    recordCancelRef.current = false;
    recordFinalizedRef.current = true;
    const durationMs = Math.min(recordSecondsRef.current, MAX_AUDIO_MS / 1000) * 1000;
    const blob = new Blob(recordChunksRef.current, { type: rec.mimeType || "audio/webm" });
    recordChunksRef.current = [];
    setRecordSeconds(0);
    recordSecondsRef.current = 0;
    if (wasCancelled) return;
    try {
      const file = new File([blob], `voice-${Date.now()}.webm`, { type: blob.type || "audio/webm" });
      handleUploadFile(file, durationMs);
        } catch (err) {
          console.error("Failed to build voice file:", err);
        }
      };

      rec.start();
      setRecording(true);
      recordTimerRef.current = setInterval(() => {
        recordSecondsRef.current += 1;
        setRecordSeconds(recordSecondsRef.current);
        if (recordSecondsRef.current >= MAX_AUDIO_MS / 1000) {
          stopRecording();
        }
      }, 1000);

      recordTimeoutRef.current = setTimeout(() => {
        stopRecording();
      }, MAX_AUDIO_MS);
    } catch (e) {
      console.error("Failed to start recording:", e);
      stopRecording({ cancel: true });
      setRecording(false);
      setRecordSeconds(0);
      recordSecondsRef.current = 0;
      window.alert(t("Unable to start recording. Please check microphone permissions."));
    }
  };

  const cancelRecording = () => {
    if (!recording) return;
    stopRecording({ cancel: true });
  };

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      stopRecording({ cancel: true });
      clearRecordingTimers();
      stopRecordingStream();
    };
  }, []);

  const renderMessageContent = (msg) => {
    const type = msg.type || "text";
    const text = msg.text || "";
    const url = msg.url || "";
    const thumb = msg.thumbnailUrl || "";
    const preview = msg.id ? uploadPreviews[msg.id] : null;
    const progress = msg.id ? uploadProgress[msg.id] : null;

    const renderUploadingOverlay = () => (
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(15, 23, 42, 0.25)",
        }}
      >
        <CircularProgress
          variant={progress != null ? "determinate" : "indeterminate"}
          value={progress != null ? Math.max(1, Math.min(100, Math.round(progress))) : undefined}
          thickness={4}
          size={54}
          sx={{ color: "#ffffff" }}
        />
      </Box>
    );

    if (type === "image") {
      if (!url && msg.status === "uploading" && preview?.url) {
        return (
          <Box sx={{ maxWidth: 320, position: "relative" }}>
            <Box
              component="img"
              src={preview.url}
              alt={t("Image")}
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 2,
                border: "1px solid #e5e7eb",
                display: "block",
                filter: "blur(3px)",
                transform: "scale(1.02)",
              }}
            />
            {renderUploadingOverlay()}
          </Box>
        );
      }
      return url ? (
        <Box sx={{ maxWidth: 320 }}>
          <Box
            component="img"
            src={url}
            alt={t("Image")}
            sx={{
              width: "100%",
              height: "auto",
              borderRadius: 2,
              border: "1px solid #e5e7eb",
              display: "block",
            }}
            loading="lazy"
          />
        </Box>
      ) : (
        <Typography sx={{ color: "#6b7280" }}>{t("Image")}</Typography>
      );
    }

    if (type === "video") {
      if (!url && msg.status === "uploading" && preview?.url) {
        return (
          <Box sx={{ maxWidth: 340, position: "relative" }}>
            <Box
              component="video"
              src={preview.url}
              muted
              playsInline
              style={{
                width: "100%",
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                display: "block",
                filter: "blur(3px)",
                transform: "scale(1.02)",
              }}
            />
            {renderUploadingOverlay()}
          </Box>
        );
      }
      return url ? (
        <Box sx={{ maxWidth: 340 }}>
          <Box
            component="video"
            src={url}
            poster={thumb || undefined}
            controls
            style={{ width: "100%", borderRadius: 12, border: "1px solid #e5e7eb" }}
          />
        </Box>
      ) : (
        <Typography sx={{ color: "#6b7280" }}>{t("Video")}</Typography>
      );
    }

    if (type === "audio") {
      return url ? (
        <Box sx={{ minWidth: 220, maxWidth: 320 }}>
          <audio controls style={{ width: "100%" }}>
            <track kind="captions" />
            <source src={url} type={msg.mimeType || "audio/mpeg"} />
            {t("Your browser does not support the audio element.")}
          </audio>
        </Box>
      ) : (
        <Typography sx={{ color: "#6b7280" }}>{t("Voice message")}</Typography>
      );
    }

    return (
      <Typography
        dir="auto"
        sx={{
          whiteSpace: "pre-wrap",
          textAlign: "start",
          color: "#152355",
          fontSize: 13,
          fontWeight: 500,
          lineHeight: 1.5,
        }}
      >
        {text}
      </Typography>
    );
  };

  return (
    <StudentLayout fullHeight>
      <Box
        sx={{
          height: "100%",
          minHeight: 0,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          px: { xs: 1, md: 1.5 },
          pt: { xs: 1.25, md: 1.7 },
          pb: { xs: 1, md: 1.25 },
          gap: { xs: 1.25, md: 1.45 },
          backgroundColor: "#fbfdff",
        }}
      >
        <Breadcrumbs
          separator="/"
          sx={{
            display: { xs: mobileView === "list" ? "flex" : "none", md: "flex" },
            flexShrink: 0,
            color: "#304c84",
            fontSize: { xs: 13, md: 14.5 },
            fontWeight: 500,
            lineHeight: 1,
            "& .MuiBreadcrumbs-ol": {
              flexDirection: isRtl ? "row-reverse" : "row",
            },
            "& .MuiBreadcrumbs-separator": {
              color: "#304c84",
              mx: { xs: 0.75, md: 1.5 },
            },
          }}
        >
          <Link underline="none" color="inherit" href="/student/classes">
            {t("My Classes")}
          </Link>
          <Typography sx={{ color: "#152355", fontSize: "inherit", fontWeight: 600 }}>
            {t("Chat")}
          </Typography>
        </Breadcrumbs>

        <Box
          sx={{
            display: "flex",
            flexDirection: isRtl ? "row-reverse" : "row",
            flex: 1,
            minHeight: 0,
            gap: { xs: 0, md: 1.25 },
          }}
        >
          {/* LEFT SIDEBAR */}
          <Box
            sx={{
              width: { xs: "100%", md: 330, lg: 350 },
              border: "1px solid #dce8f8",
              borderRadius: "14px",
              boxShadow: "0 10px 28px rgba(8, 26, 74, 0.025)",
              backgroundColor: "#ffffff",
              overflow: "hidden",
              display: { xs: mobileView === "list" ? "flex" : "none", md: "flex" },
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                px: { xs: 1.2, md: 1.3 },
                pt: { xs: 1.25, md: 1.35 },
                pb: 1,
                display: "flex",
                flexDirection: "column",
                gap: 1.05,
              }}
            >
              <Typography sx={{ color: "#071339", fontSize: 14.5, fontWeight: 800, textAlign: "start" }}>
                {t("Class Conversations")}
              </Typography>
              <TextField
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                placeholder={t("Search students or messages...")}
                size="small"
                InputProps={{
                  ...(isRtl
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <SearchRoundedIcon sx={{ color: "#42659d", fontSize: 18 }} />
                          </InputAdornment>
                        ),
                      }
                    : {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchRoundedIcon sx={{ color: "#42659d", fontSize: 18 }} />
                          </InputAdornment>
                        ),
                      }),
                  sx: {
                    height: 40,
                    direction: isRtl ? "rtl" : "ltr",
                    borderRadius: "10px",
                    color: "#152355",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#dce8f8",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#c8d8ef",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#9bb9e7",
                      borderWidth: 1,
                    },
                    "& .MuiInputBase-input": {
                      textAlign: isRtl ? "right" : "left",
                      fontSize: 12,
                      fontWeight: 500,
                      "&::placeholder": {
                        color: "#7d8797",
                        opacity: 1,
                      },
                    },
                  },
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: isRtl ? "row-reverse" : "row",
                  gap: 0.65,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Chip
                  label={t("All")}
                  size="small"
                  sx={{
                    height: 26,
                    borderRadius: "8px",
                    bgcolor: "#edf5ff",
                    color: "#0068ff",
                    fontSize: 11.5,
                    fontWeight: 700,
                    "& .MuiChip-label": { px: 1.4 },
                  }}
                />
                <Chip
                  label={`${t("Unread")} ${allTeacherRows.reduce((sum, row) => sum + Number(row.unreadCountStudent || 0), 0)}`}
                  size="small"
                  sx={{
                    height: 26,
                    borderRadius: "8px",
                    bgcolor: "#f7faff",
                    color: "#304c84",
                    border: "1px solid #dce8f8",
                    fontSize: 11.5,
                    fontWeight: 700,
                    "& .MuiChip-label": { px: 1.2 },
                  }}
                />
                <Chip
                  label={t("Recent")}
                  size="small"
                  sx={{
                    height: 26,
                    borderRadius: "8px",
                    bgcolor: "#f7faff",
                    color: "#304c84",
                    border: "1px solid #dce8f8",
                    fontSize: 11.5,
                    fontWeight: 700,
                    "& .MuiChip-label": { px: 1.35 },
                  }}
                />
              </Box>
            </Box>

            {/* Teachers list */}
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: { xs: 0.75, md: 0.85 }, pb: 1.1 }}>
              <List sx={{ p: 0, display: "grid", gap: 0.15 }}>
                {filteredTeachers.length === 0 ? (
                  <Box sx={{ p: 2 }}>
                    <Typography sx={{ color: "#6f89b8", fontSize: 13 }}>{t("No teachers found.")}</Typography>
                  </Box>
                ) : (
                  filteredTeachers.map((row) => {
                    const unread = Number(row.unreadCountStudent || 0);
                    const isActive = activeConversation?.teacherId === row.teacherId;
                    const rowTime = safeTime(row.lastMessageAt);

                    return (
                      <ListItemButton
                        key={row.teacherId}
                        selected={isActive}
                        onClick={() =>
                          openOrCreateConversation({
                            teacherId: row.teacherId,
                            teacherName: row.teacherName,
                            teacherPhotoURL: row.teacherPhotoURL,
                          })
                        }
                        sx={{
                          px: 0.85,
                          py: 0.9,
                          borderRadius: "9px",
                          alignItems: "center",
                          direction: isRtl ? "rtl" : "ltr",
                          gap: 1,
                          "&.Mui-selected": { backgroundColor: "#edf5ff" },
                          "&.Mui-selected:hover": { backgroundColor: "#e5f1ff" },
                          "&:hover": { backgroundColor: "#f7faff" },
                        }}
                      >
                        <Avatar
                          src={row.teacherPhotoURL || undefined}
                          alt={row.teacherName || t("Teacher")}
                          sx={{
                            width: 44,
                            height: 44,
                            bgcolor: avatarColorsForName(row.teacherName || t("Teacher")).bg,
                            color: avatarColorsForName(row.teacherName || t("Teacher")).color,
                            fontWeight: 800,
                            fontSize: 13,
                            flexShrink: 0,
                          }}
                        >
                          {initialsFromName(row.teacherName || t("Teacher"))}
                        </Avatar>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              color: "#071339",
                              fontSize: 12.5,
                              textAlign: "start",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.teacherName || t("Teacher")}
                          </Typography>

                          <Typography
                            sx={{
                              color: "#304c84",
                              fontSize: 11.25,
                              fontWeight: 500,
                              mt: 0.25,
                              textAlign: "start",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {previewText(row)}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            gap: 0.9,
                            alignSelf: "stretch",
                            justifyContent: "space-between",
                            flexShrink: 0,
                            py: 0.2,
                          }}
                        >
                          <Typography sx={{ color: "#304c84", fontSize: 10.5, fontWeight: 500, textAlign: "end" }}>
                            {rowTime}
                          </Typography>
                          {unread > 0 && (
                            <Box
                              sx={{
                                minWidth: 19,
                                height: 19,
                                px: 0.55,
                                borderRadius: "999px",
                                bgcolor: "#0068ff",
                                color: "#ffffff",
                                display: "grid",
                                placeItems: "center",
                                fontSize: 10,
                                fontWeight: 800,
                                lineHeight: 1,
                              }}
                            >
                              {unread > 99 ? "99+" : unread}
                            </Box>
                          )}
                        </Box>
                      </ListItemButton>
                    );
                  })
                )}
              </List>
            </Box>
          </Box>

          {/* RIGHT CHAT AREA */}
          <Box
            sx={{
              flex: 1,
              display: { xs: mobileView === "chat" ? "flex" : "none", md: "flex" },
              flexDirection: "column",
              backgroundColor: "#ffffff",
              border: "1px solid #dce8f8",
              borderRadius: "14px",
              boxShadow: "0 10px 28px rgba(8, 26, 74, 0.025)",
              overflow: "hidden",
              minWidth: 0,
            }}
          >
            {!activeConversation ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  px: 3,
                  backgroundColor: "#ffffff",
                }}
              >
                <EmailIcon sx={{ fontSize: 48, color: "#8aa4ca", mb: 1.5 }} />
                <Typography sx={{ fontWeight: 800, fontSize: 18, color: "#071339", mb: 0.7 }}>
                  {t("Select a conversation")}
                </Typography>
                <Typography sx={{ color: "#6f89b8", maxWidth: 260, fontSize: 13.5 }}>
                  {t("Choose a teacher from the left to view messages.")}
                </Typography>
              </Box>
            ) : (
              <>
                {/* Header */}
                <Box
                  sx={{
                    px: { xs: 1.05, md: 1.2 },
                    py: 0.95,
                    display: "flex",
                    flexDirection: isRtl ? "row-reverse" : "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.1,
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: isRtl ? "row-reverse" : "row", alignItems: "center", gap: 1.1, minWidth: 0 }}>
                    {isMobile && (
                      <IconButton
                        size="small"
                        onClick={() => setMobileView("list")}
                        sx={{ mx: 0.5 }}
                        aria-label={t("Back to list")}
                      >
                        <ArrowBackIosNewIcon
                          fontSize="small"
                          sx={{ transform: isRtl ? "rotate(180deg)" : "none" }}
                        />
                      </IconButton>
                    )}

                    <Avatar
                      src={activeConversation.teacherPhotoURL || undefined}
                      alt={activeConversation.teacherName || t("Teacher")}
                      sx={{
                        width: 42,
                        height: 42,
                        bgcolor: avatarColorsForName(activeConversation.teacherName || t("Teacher")).bg,
                        color: avatarColorsForName(activeConversation.teacherName || t("Teacher")).color,
                        fontWeight: 800,
                        objectFit: "cover",
                        border: "1.5px solid #0068ff",
                        fontSize: 13,
                      }}
                    >
                      {initialsFromName(activeConversation.teacherName || t("Teacher"))}
                    </Avatar>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          color: "#071339",
                          fontSize: 13,
                          textAlign: "start",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {activeConversation.teacherName || t("Teacher")}
                      </Typography>

                      <Box sx={{ display: "flex", flexDirection: isRtl ? "row-reverse" : "row", alignItems: "center", gap: 0.75, mt: 0.25 }}>
                        <Box sx={{ width: 6.5, height: 6.5, borderRadius: "999px", bgcolor: "#17c964" }} />
                        <Typography sx={{ color: "#42659d", fontSize: 11.25, fontWeight: 500 }}>{t("Online now")}</Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", flexDirection: isRtl ? "row-reverse" : "row", alignItems: "center", gap: 0.25 }}>
                    <Tooltip title={t("Search")}>
                      <IconButton size="small" sx={{ color: "#2f5ca8", width: 30, height: 30 }}>
                        <SearchRoundedIcon sx={{ fontSize: 19 }} />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={t("More")}>
                      <IconButton size="small" sx={{ color: "#2f5ca8", width: 30, height: 30 }}>
                        <MoreVertIcon sx={{ fontSize: 19 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Divider />

                {/* Messages */}
                <Box
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                    px: { xs: 1.2, md: 1.4 },
                    py: { xs: 2, md: 2.4 },
                    backgroundColor: "#ffffff",
                  }}
                  ref={messagesListRef}
                  onScroll={handleMessagesScroll}
                >
                  {groupedMessages.length === 0 ? (
                    <Typography sx={{ color: "#6b7280" }}>{t("No messages yet.")}</Typography>
                  ) : (
                    groupedMessages.map((item) => {
                      if (item.type === "sep") {
                        return (
                          <Box
                            key={item.id}
                            sx={{ display: "flex", alignItems: "center", justifyContent: "center", my: 2 }}
                          >
                            <Typography
                              sx={{
                                backgroundColor: "#ffffffff",
                                color: "#888686ff",
                                fontSize: 12,
                                fontWeight: 800,
                                px: 2,
                                py: 0.6,
                                borderRadius: "999px",
                                border: "1px solid #e5e7eb",
                              }}
                            >
                              {item.label}
                            </Typography>
                          </Box>
                        );
                      }

                      const isMine = item.senderId === studentDocId;
                      const messageTime = safeTime(item.createdAt);
                      const bubbleTone = avatarColorsForName(activeConversation?.teacherName || t("Teacher"));
                      const studentTone = avatarColorsForName(studentProfile.name || t("Student"));
                      const messageType = item.type || "text";
                      const isAttachment = ["image", "video", "audio"].includes(messageType);
                      const isSeenByTeacher = isMine && !!item.seenByTeacherAt;
                      const messageBody = isAttachment ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: isMine ? "flex-end" : "flex-start",
                            maxWidth: "100%",
                            minWidth: 0,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: isMine ? "row-reverse" : "row",
                              alignItems: "center",
                              gap: 0.9,
                              maxWidth: "100%",
                              alignSelf: isMine ? "flex-end" : "flex-start",
                            }}
                          >
                            <Box sx={{ bgcolor: "transparent", px: 0, py: 0 }}>
                              {renderMessageContent(item)}
                            </Box>
                            {!!messageTime && (
                              <Typography sx={{ fontSize: 11, color: "#49699f", fontWeight: 500, flexShrink: 0 }}>
                                {messageTime}
                              </Typography>
                            )}
                          </Box>
                          {isSeenByTeacher && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                mt: 0.5,
                                px: 0.2,
                                alignSelf: "flex-end",
                              }}
                            >
                              <VisibilityOutlinedIcon sx={{ fontSize: 13, color: "#0068ff" }} />
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <ChatBubble
                          isMine={isMine}
                          time={messageTime}
                          seen={isSeenByTeacher}
                          status={item.status}
                          progress={uploadProgress[item.id]}
                        >
                          {renderMessageContent(item)}
                        </ChatBubble>
                      );

                      return (
                        <Box
                          className="student-message-row"
                          key={item.id}
                          sx={{
                            display: "flex",
                            justifyContent: isMine ? "flex-end" : "flex-start",
                            mb: 2.2,
                            gap: 1,
                            alignItems: "center",
                            minWidth: 0,
                            "&:hover .student-message-options": {
                              opacity: 1,
                              pointerEvents: "auto",
                            },
                          }}
                        >
                          {!isMine && (
                            <Avatar
                              src={activeConversation?.teacherPhotoURL || undefined}
                              alt={activeConversation?.teacherName || t("Teacher")}
                              sx={{
                                width: 34,
                                height: 34,
                                bgcolor: bubbleTone.bg,
                                color: bubbleTone.color,
                                fontWeight: 800,
                                fontSize: 13,
                                flexShrink: 0,
                              }}
                            >
                              {initialsFromName(activeConversation?.teacherName || t("Teacher"))}
                            </Avatar>
                          )}

                          {messageBody}

                          {isMine && (
                            <>
                              <IconButton
                                size="small"
                                className="student-message-options"
                                onClick={(event) => {
                                  setMessageMenuAnchor(event.currentTarget);
                                  setActiveMessageId(item.id);
                                  setActiveMessageSenderId(item.senderId || null);
                                }}
                                aria-label={t("Message options")}
                                sx={{
                                  color: "#8aa4ca",
                                  opacity: { xs: 0.45, md: 0 },
                                  pointerEvents: { xs: "auto", md: "none" },
                                  transition: "opacity 160ms ease",
                                }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                              <Avatar
                                src={studentProfile.photoURL || undefined}
                                alt={studentProfile.name || t("Student")}
                                sx={{
                                  width: 34,
                                  height: 34,
                                  bgcolor: studentTone.bg,
                                  color: studentTone.color,
                                  fontWeight: 800,
                                  fontSize: 13,
                                  flexShrink: 0,
                                }}
                              >
                                {initialsFromName(studentProfile.name || t("Student"))}
                              </Avatar>
                            </>
                          )}
                        </Box>
                      );
                    })
                  )}
                </Box>

                <Menu
                  anchorEl={messageMenuAnchor}
                  open={Boolean(messageMenuAnchor)}
                  onClose={() => {
                    setMessageMenuAnchor(null);
                    setActiveMessageId(null);
                    setActiveMessageSenderId(null);
                  }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  {activeMessageSenderId === studentDocId && (
                    <MenuItem
                      dense
                      onClick={async () => {
                        if (!activeConversation?.id || !activeMessageId || activeMessageSenderId !== studentDocId) {
                          setMessageMenuAnchor(null);
                          setActiveMessageId(null);
                          setActiveMessageSenderId(null);
                          return;
                        }
                        try {
                          await deleteDoc(
                            doc(db, "conversations", activeConversation.id, "messages", activeMessageId)
                          );
                          setMessages((prev) => prev.filter((m) => m.id !== activeMessageId));
                        } catch (err) {
                          console.error("Failed to delete message:", err);
                        } finally {
                          setMessageMenuAnchor(null);
                          setActiveMessageId(null);
                          setActiveMessageSenderId(null);
                        }
                      }}
                    >
                      {t("Delete message")}
                    </MenuItem>
                  )}
                </Menu>

                <Divider />

                {/* Composer */}
                <Box
                  sx={{
                    px: { xs: 1.05, md: 1.2 },
                    py: 1,
                    display: "flex",
                    flexDirection: isRtl ? "row-reverse" : "row",
                    alignItems: "center",
                    gap: 0.65,
                    backgroundColor: "#ffffff",
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    hidden
                    onChange={handleAttachmentChange}
                  />

                  <Tooltip title={t("Attach file")}>
                    <span>
                      <IconButton
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading || recording}
                        sx={{
                          width: 30,
                          height: 38,
                          borderRadius: "8px",
                          bgcolor: "transparent",
                          color: "#0068ff",
                          p: 0,
                          "&:hover": { bgcolor: "rgba(0, 104, 255, 0.06)" },
                        }}
                      >
                        <AttachFileRoundedIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title={recording ? t("Stop recording") : t("Voice message")}>
                    <span>
                      <IconButton
                        color={recording ? "error" : "default"}
                        onClick={recording ? () => stopRecording() : startRecording}
                        disabled={uploading}
                        sx={{
                          width: 30,
                          height: 38,
                          borderRadius: "8px",
                          bgcolor: "transparent",
                          color: recording ? "#ef4444" : "#0068ff",
                          p: 0,
                          "&:hover": { bgcolor: recording ? "rgba(239, 68, 68, 0.08)" : "rgba(0, 104, 255, 0.06)" },
                        }}
                      >
                        {recording ? <StopRoundedIcon sx={{ fontSize: 20 }} /> : <KeyboardVoiceRoundedIcon sx={{ fontSize: 20 }} />}
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Popover
                    open={isEmojiPickerOpen}
                    anchorEl={emojiAnchorEl}
                    onClose={() => setEmojiAnchorEl(null)}
                    anchorOrigin={{ vertical: "top", horizontal: isRtl ? "right" : "left" }}
                    transformOrigin={{ vertical: "bottom", horizontal: isRtl ? "right" : "left" }}
                  >
                    <EmojiPicker
                      onEmojiClick={handleEmojiPick}
                      lazyLoadEmojis
                      skinTonesDisabled
                    />
                  </Popover>

                  {recording && (
                    <Chip
                      size="small"
                      label={t("{{count}}s / 120s", { count: recordSeconds })}
                      onDelete={cancelRecording}
                      deleteIcon={<CloseRoundedIcon />}
                      color="error"
                      variant="outlined"
                      sx={{ mx: 0.5 }}
                    />
                  )}

                  <TextField
                    inputRef={composerInputRef}
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    fullWidth
                    placeholder={t("Type your message...")}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title={t("Emoji")}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={(event) => setEmojiAnchorEl(event.currentTarget)}
                                disabled={uploading}
                                sx={{ color: "#8aa4ca" }}
                              >
                                <EmojiEmotionsOutlinedIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        minHeight: 42,
                        borderRadius: "10px",
                        bgcolor: "#ffffff",
                        color: "#152355",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#dce8f8",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#c8d8ef",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#9bb9e7",
                          borderWidth: 1,
                        },
                      },
                      "& .MuiOutlinedInput-input": {
                        textAlign: isRtl ? "right !important" : "left !important",
                        direction: isRtl ? "rtl !important" : "ltr !important",
                        unicodeBidi: "plaintext",
                        padding: "10px 13px",
                        color: "#152355",
                        fontSize: 12.25,
                        fontWeight: 500,
                      },
                      "& .MuiOutlinedInput-input::placeholder": {
                        textAlign: isRtl ? "right" : "left",
                        opacity: 1,
                        color: "#7d8797",
                      },
                    }}
                  />

                  <Button
                    variant="contained"
                    disabled={sending || !messageInput.trim()}
                    onClick={handleSend}
                    sx={{
                      height: 42,
                      borderRadius: "10px",
                      backgroundColor: "#0068ff",
                      whiteSpace: "nowrap",
                      px: 0,
                      minWidth: 46,
                      width: 46,
                      fontWeight: 700,
                      fontSize: 12.75,
                      textTransform: "none",
                      boxShadow: "0 8px 18px rgba(0, 104, 255, 0.2)",
                      "&:hover": {
                        backgroundColor: "#005ce0",
                        boxShadow: "0 10px 22px rgba(0, 104, 255, 0.24)",
                      },
                    }}
                    aria-label={t("Send")}
                  >
                    <TelegramIcon sx={{ fontSize: 21, transform: isRtl ? "scaleX(-1)" : "none" }} />
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </StudentLayout>
  );
}
